import {observer} from 'mobx-react-lite';

import {Subline} from 'UI/components/layout/Text';

/**
 * The `ComponentProps` interface defines the properties for the `SublineRichtext` component.
 * This interface specifies the structure of props required for rendering rich text content as a subline element.
 * It includes a fields object containing the content data and an optional test identifier for component testing purposes.
 */
interface ComponentProps {
    /**
     * The fields object containing the content data to be displayed in the subline component.
     * This object must include a content property with the string value that will be rendered as rich text.
     * The content field supports plain text that will be displayed within the Subline wrapper component.
     */
    fields: {
        /**
         * The text content to be displayed in the subline element.
         * This string value represents the actual text that will be rendered within the component.
         * The content should be a valid string that provides meaningful information to the user.
         */
        content: string;
    };
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios and automated test suites.
     * When provided, it enables precise component selection and interaction during testing procedures.
     *
     * @default 'SublineRichtext'
     */
    testId?: string;
}

/**
 * The `SublineRichtext` component renders rich text content as a subline element with reactive updates.
 * This component serves as a wrapper around the Subline component to display formatted text content from a fields object.
 * It utilizes MobX observer pattern to automatically re-render when the underlying data changes, making it suitable for dynamic content scenarios.
 *
 * @param props        The component props.
 * @param props.fields The fields object containing the content to be displayed.
 * @returns The SublineRichtext component JSX element.
 *
 * @example
 * ```tsx
 * <SublineRichtext
 *     fields={{ content: "This is a subline text" }}
 *     testId="my-subline"
 * />
 * ```
 */
const SublineRichtext = observer(({fields}: ComponentProps) => (
    <Subline>{fields.content}</Subline>
));

SublineRichtext.displayName = 'SublineRichtext';

export {SublineRichtext};